import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { brl, data } from "@/lib/format";
import {
  PageHeader,
  Panel,
  PanelHeader,
  Th,
  Td,
  EmptyState,
  Stat,
  StatusBadge,
  Erro,
  GradeFios,
} from "@/components/parker";
import { useEmpresas, useIntercompany, useIndice } from "@/lib/dados";
import { liquidarIntercompany } from "@/lib/fns";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { baixarCSV, centavosParaCSV } from "@/lib/exportar";

export const Route = createFileRoute("/_authenticated/intercompany")({ component: Intercompany });

function Intercompany() {
  const { podeEscrever } = useSessao();
  const empresas = useEmpresas();
  const obrigacoes = useIntercompany();
  const nomeEmpresa = useIndice(empresas.dados);
  const [ocupado, setOcupado] = useState<string | null>(null);

  if (obrigacoes.erro) return <Erro mensagem={obrigacoes.erro} />;

  const abertos = obrigacoes.dados.filter((r) => r.status === "aberto");
  const total = abertos.reduce((s, r) => s + Number(r.valorCentavos), 0);

  // Agrupa por par devedora→credora: é assim que o acerto entre as empresas é feito.
  const porPar = Object.values(
    abertos.reduce<
      Record<string, { chave: string; de: string; para: string; valor: number; n: number }>
    >((acc, r) => {
      const chave = `${r.empresaDevedoraId}→${r.empresaCredoraId}`;
      acc[chave] ??= {
        chave,
        de: nomeEmpresa.get(r.empresaDevedoraId) ?? "—",
        para: nomeEmpresa.get(r.empresaCredoraId) ?? "—",
        valor: 0,
        n: 0,
      };
      acc[chave]!.valor += Number(r.valorCentavos);
      acc[chave]!.n += 1;
      return acc;
    }, {}),
  ).sort((a, b) => b.valor - a.valor);

  async function liquidar(id: string) {
    setOcupado(id);
    try {
      await liquidarIntercompany({ id });
      toast.success("Obrigação liquidada.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  function exportar() {
    baixarCSV(
      "intercompany",
      ["Origem", "Competência", "Devedora", "Credora", "Valor", "Status"],
      obrigacoes.dados.map((r) => [
        r.descricao,
        r.dataCompetencia,
        nomeEmpresa.get(r.empresaDevedoraId) ?? "",
        nomeEmpresa.get(r.empresaCredoraId) ?? "",
        centavosParaCSV(r.valorCentavos),
        r.status,
      ]),
    );
  }

  return (
    <>
      <PageHeader
        title="Intercompany"
        subtitle="Nasce sozinho quando uma empresa paga por outra. Eliminado no consolidado do grupo."
        actions={
          <Button variant="outline" onClick={exportar} disabled={obrigacoes.dados.length === 0}>
            <Download className="size-4" /> Exportar
          </Button>
        }
      />

      <GradeFios colunas={3}>
        <Stat label="Total em aberto" value={brl(total)} tone={total > 0 ? "warning" : "default"} />
        <Stat label="Obrigações abertas" value={String(abertos.length)} />
        <Stat label="Pares de empresas" value={String(porPar.length)} />
      </GradeFios>

      <Panel className="mt-6 overflow-hidden">
        <PanelHeader title="Posição por contraparte" />
        {porPar.length === 0 ? (
          <EmptyState title="Sem saldo em aberto" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Devedora</Th>
                <Th>Credora</Th>
                <Th className="text-right">Obrigações</Th>
                <Th className="text-right">Saldo</Th>
              </tr>
            </thead>
            <tbody>
              {porPar.map((p) => (
                <tr key={p.chave}>
                  <Td>{p.de}</Td>
                  <Td>{p.para}</Td>
                  <Td className="num text-right text-muted-foreground">{p.n}</Td>
                  <Td className="num text-right font-medium">{brl(p.valor)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel className="mt-4 overflow-x-auto">
        <PanelHeader title="Obrigações" />
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr>
              <Th>Origem</Th>
              <Th>Competência</Th>
              <Th>Devedora</Th>
              <Th>Credora</Th>
              <Th className="text-right">Valor</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {obrigacoes.dados.map((r) => (
              <tr key={r.id}>
                <Td>
                  <div className="max-w-[280px] truncate">{r.descricao}</div>
                </Td>
                <Td className="num whitespace-nowrap">{data(r.dataCompetencia)}</Td>
                <Td className="text-muted-foreground">
                  {nomeEmpresa.get(r.empresaDevedoraId) ?? "—"}
                </Td>
                <Td className="text-muted-foreground">
                  {nomeEmpresa.get(r.empresaCredoraId) ?? "—"}
                </Td>
                <Td className="num text-right">{brl(r.valorCentavos)}</Td>
                <Td>
                  <StatusBadge status={r.status} />
                </Td>
                <Td className="text-right">
                  {podeEscrever && r.status === "aberto" ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={ocupado === r.id}
                      onClick={() => void liquidar(r.id)}
                    >
                      Liquidar
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {obrigacoes.dados.length === 0 ? (
          <EmptyState
            title="Nenhuma obrigação intercompany"
            hint="Aparecem aqui quando uma empresa paga por outra e o lançamento é aprovado."
          />
        ) : null}
      </Panel>
    </>
  );
}
